import {
    BlockStack,
    Box,
    Card,
    Checkbox,
    InlineStack,
    Spinner,
    Text,
    Tooltip,
} from "@shopify/polaris";
import type { ReactNode } from "react";

type SettingsCardProps = {
    title: string;
    description?: string;
    children: ReactNode;
};

type SettingCheckboxProps = {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    description?: string;
    help?: string;
};

const helpIconStyle = {
    alignItems: "center",
    border: "1px solid #8a8a8a",
    borderRadius: "50%",
    color: "#616161",
    cursor: "help",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 600,
    height: 18,
    justifyContent: "center",
    lineHeight: "18px",
    width: 18,
} as const;

const settingRowStyle = {
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    padding: "14px 0",
} as const;

const settingRowTextStyle = {
    flex: "1 1 280px",
    minWidth: 0,
} as const;

const settingRowControlStyle = {
    paddingTop: 2,
} as const;

const HelpTooltip = ({ content }: { content: string }) => (
    <Tooltip content={content}>
        <span aria-label={content} role="img" style={helpIconStyle} tabIndex={0}>
            ?
        </span>
    </Tooltip>
);

export const SettingsCard = ({ title, description, children }: SettingsCardProps) => (
    <Card>
        <Box padding="400">
            <BlockStack gap="400">
                <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">
                        {title}
                    </Text>
                    {description ? (
                        <Text as="p" variant="bodySm" tone="subdued">
                            {description}
                        </Text>
                    ) : null}
                </BlockStack>
                <BlockStack gap="0">{children}</BlockStack>
            </BlockStack>
        </Box>
    </Card>
);

export const SettingCheckbox = ({ label, checked, onChange, description, help }: SettingCheckboxProps) => (
    <div style={settingRowStyle}>
        <div style={settingRowTextStyle}>
            <InlineStack gap="100" blockAlign="center">
                <Text as="span" variant="bodyMd" fontWeight="medium">
                    {label}
                </Text>
                {help ? <HelpTooltip content={help} /> : null}
            </InlineStack>
            {description ? (
                <Box paddingBlockStart="100">
                    <Text as="p" variant="bodySm" tone="subdued">
                        {description}
                    </Text>
                </Box>
            ) : null}
        </div>
        <div style={settingRowControlStyle}>
            <Checkbox label={label} labelHidden checked={checked} onChange={onChange} />
        </div>
    </div>
);

export const LoadingSettingsCard = ({ label }: { label: string }) => (
    <SettingsCard title={label}>
        <InlineStack gap="200" align="center">
            <Spinner accessibilityLabel={`Loading ${label.toLowerCase()}`} size="small" />
            <Text as="span" variant="bodyMd">
                Loading settings...
            </Text>
        </InlineStack>
    </SettingsCard>
);
